import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import NumberFormat from 'react-number-format'
import { NavLink } from 'react-router-dom'
import { Container, Columns, Column, Button } from '@resurs/midas-react'
import { Conditional } from '@resurs/react-utils'
import { connect } from 'react-redux'
import PageWrapper from '../../../../internal/PageWrapper'
import { getText } from '../../../../internal/TextLabels'

import { successAnimation } from '../../../../assets/animations'
import BarQRCode from '../../../../components/common/BarQRCode'
import BasisConfirmationForm from '../shared/BasisConfirmationForm'
import { PaymentMethods } from '../../../../models/payment-methods'
import PrintModalUI from '../shared/PrintModalUI'
import { getBasisConfirmationPdf } from '../../../../store/actions/basis-actions'
import {
  setCurrentPage,
  setCurrentStep,
  setDebitReference,
  setDebitAmount,
  resetCurrentPaymentMethod,
  selectPaymentMethodAndSetPaymentData,
  createPayment,
  setFormSpecData,
  selectPaymentMethod,
  debitPayment,
} from '../../../../store/actions/pay-apply-actions'
import { getCardOrAccountNumber } from '../shared/utils'
import DebitOnDone from './DebitOnDone'
import { PageModel, PathModel } from '../../models/private-models'
import LoaderOverlay from '../../../../internal/LoaderOverlay'
import { ApplicationStatus } from '../../../../models/application-status'

const ApplicationDonePage = props => {
  const [name, setName] = useState(null)
  const [pdfUrl, setPdfUrl] = useState(null)

  const [showDebitButton, setShowDebitButton] = useState(false)
  const [disableDebitButton, setDisableDebitButton] = useState(true)
  const [debitInfo, setDebitInfo] = useState({})
  const [result] = useState({ ...props.data.application.applicationResult })

  const isDebiting = useRef(false)
  const isTimeToCreatePayment = useRef(false)

  const onDebit = () => {
    isDebiting.current = true
    props.resetCurrentPaymentMethod()
  }

  useEffect(() => {
    if (
      isDebiting.current &&
      props.data.current.selectedPaymentMethod &&
      !isTimeToCreatePayment.current
    ) {
      props.selectPaymentMethodAndSetPaymentData({
        debitPaymentChecked: true,
        amount: debitInfo.debitAmount,
        selectedLimit: 0,
        selectedPaymentMethod: debitInfo.selectedPaymentMethod,
        selectedCard: debitInfo.selectedCard,
        debitOnMethodId: debitInfo.selectedPaymentMethod.id,
        debitReference: debitInfo.debitReference,
        accountNumber:
          debitInfo.selectedPaymentMethod.accounts[0].accountNumber,
      })
      props.setFormSpecData(debitInfo.formSpecData)
      isTimeToCreatePayment.current = true
    } else if (
      isDebiting.current &&
      !props.data.current.selectedPaymentMethod
    ) {
      props.selectPaymentMethod({
        selectedPaymentMethod: debitInfo.selectedPaymentMethod,
      })
    }
  }, [props.data.current])

  useEffect(() => {
    if (isTimeToCreatePayment.current) {
      props.createPayment()
      isTimeToCreatePayment.current = false
    }
  }, [props.data.current.selectedPaymentMethod])

  useEffect(() => {
    if (props.data.payment.paymentResult) {
      if (
        props.data.payment.paymentResult.status ===
        ApplicationStatus.SESSION_ALIVE
      ) {
        props.debitPayment(props.data.payment.paymentResult.id)
      }

      if (
        [
          ApplicationStatus.SIGNING_REQUIRED,
          ApplicationStatus.CUSTOMER_INTERACTION_REQUIRED,
        ].includes(props.data.payment.paymentResult.status)
      ) {
        props.setCurrentPage(PageModel.APPLICATION_SIGN)
        props.setCurrentStep(2)

        props.history.push(PathModel[PageModel.APPLICATION_SIGN])
      }
    }
  }, [props.data.payment])

  useEffect(() => {
    if (!isDebiting.current) {
      const { data } = props
      if (
        data &&
        data.application &&
        data.application.applicationResult &&
        data.application.applicationResult.account
      ) {
        const cards = data.paymentMethods
          .filter(method => method.type === PaymentMethods.RESURS_CARD)
          .map(method =>
            method.availableCards.find(
              c =>
                c.cardHolderName &&
                c.accountNumber ===
                  data.application.applicationResult.account.accountNumber,
            ),
          )
        const card = cards && cards.length && cards[0]
        if (card) {
          setName(card.cardHolderName)
        }
      }
    }
  }, [props.data])

  const getConfirmationPdf = data => {
    props
      .getBasisConfirmationPdf(data, 'Resurs_Confirmation')
      .then(url => url && setPdfUrl(url))
  }

  const render = () => {
    const cardOrAccountNumber = result
      ? getCardOrAccountNumber(result.account)
      : {}
    return (
      <LoaderOverlay loading={isDebiting.current}>
        <Conditional when={!!pdfUrl}>
          {() => (
            <PrintModalUI
              url={pdfUrl}
              show={!!pdfUrl}
              closeText={getText('default.close')}
              onClose={() => setPdfUrl(null)}
            />
          )}
        </Conditional>
        <PageWrapper type="success">
          <div
            style={{
              padding: '2rem',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ display: 'flex' }}>
              <img
                style={{ margin: '1rem' }}
                src={successAnimation}
                alt="Success animation"
              />
              <h4 style={{ margin: '2.2rem 1rem 0 0' }}>
                {getText('payApply.applicationDone.title')}
              </h4>
            </div>
            <Columns>
              <Column col6 colMd12>
                <div style={{ paddingLeft: '6rem' }}>
                  {getText('payApply.applicationDone.requestedAmount')}{' '}
                  <strong>
                    <NumberFormat
                      value={
                        result.application
                          ? result.application.requestedAmount
                          : 0
                      }
                      displayType="text"
                      thousandSeparator=" "
                      renderText={val => getText('default.currency', val)}
                    />
                  </strong>
                  <div style={{ padding: '.5rem 0 0 0' }}>
                    {getText('payApply.applicationDone.approvedAmount')}{' '}
                    <strong>
                      <NumberFormat
                        value={
                          result.application
                            ? result.application.approvedAmount
                            : 0
                        }
                        displayType="text"
                        thousandSeparator=" "
                        renderText={val => getText('default.currency', val)}
                      />
                    </strong>
                  </div>
                </div>
              </Column>
              <Column col6 colMd12>
                <div>
                  {getText('payApply.applicationDone.useBarcodeInCheckout')}
                </div>
                <div>
                  {getText('payApply.applicationDone.newApplicationOrLogout')}
                </div>
              </Column>
            </Columns>
          </div>
          <Container key="info">
            <Columns>
              <Column col12>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '0rem 1rem 1rem',
                  }}
                >
                  <BasisConfirmationForm
                    card={{
                      cardNumber: cardOrAccountNumber,
                      cardHolderName: name,
                    }}
                    printBasisConfirmation={data => getConfirmationPdf(data)}
                  />
                  <div
                    style={{
                      marginTop: '2rem',
                      padding: '2rem',
                      border: '1px solid lightgrey',
                      minHeight: '160px',
                    }}
                  >
                    <BarQRCode accountNumber={cardOrAccountNumber} />
                    <br />
                    <div
                      style={{
                        display: 'flex',
                        fontSize: '3rem',
                        justifyContent: 'center',
                        width: '100%',
                      }}
                    >
                      <div style={{ paddingRight: '0.5rem' }}>
                        {getText('payApply.applicationDone.accountNumber')}
                      </div>
                      <NumberFormat
                        value={
                          result.account ? result.account.accountNumber : ''
                        }
                        displayType="text"
                        format="#### #### #### ####"
                      />
                    </div>
                  </div>
                  <DebitOnDone
                    onDebitOpen={isOpen => setShowDebitButton(isOpen)}
                    onValidAmount={valid => setDisableDebitButton(!valid)}
                    onChange={obj => setDebitInfo(obj)}
                  />
                </div>
              </Column>
            </Columns>
          </Container>
        </PageWrapper>
        <PageWrapper>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '2rem',
            }}
          >
            <NavLink to="/apply">
              <Button>{getText('payApply.restart')}</Button>
            </NavLink>
            <Conditional when={!showDebitButton}>
              <Button type="primary" onClick={() => props.onNext()}>
                {getText('payApply.logout')}
              </Button>
            </Conditional>
            <Conditional when={showDebitButton}>
              <Button
                type="primary"
                onClick={() => {
                  onDebit()
                }}
                disabled={disableDebitButton}
              >
                {getText('transactions.transaction.action.debit')}
              </Button>
            </Conditional>
          </div>
        </PageWrapper>
      </LoaderOverlay>
    )
  }

  return render()
}
ApplicationDonePage.propTypes = {
  getBasisConfirmationPdf: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  setDebitReference: PropTypes.func.isRequired,
  setDebitAmount: PropTypes.func.isRequired,
  selectPaymentMethodAndSetPaymentData: PropTypes.func.isRequired,
  resetCurrentPaymentMethod: PropTypes.func.isRequired,
  createPayment: PropTypes.func.isRequired,
  setFormSpecData: PropTypes.func.isRequired,
  selectPaymentMethod: PropTypes.func.isRequired,
  debitPayment: PropTypes.func.isRequired,
}

const mapDispatchToProps = dispatch => ({
  getBasisConfirmationPdf: data => dispatch(getBasisConfirmationPdf(data)),
  setCurrentPage: index => dispatch(setCurrentPage(index)),
  setCurrentStep: step => dispatch(setCurrentStep(step)),
  setDebitReference: ref => dispatch(setDebitReference(ref)),
  setDebitAmount: amount => dispatch(setDebitAmount(amount)),
  selectPaymentMethodAndSetPaymentData: paymentData =>
    dispatch(selectPaymentMethodAndSetPaymentData(paymentData)),
  resetCurrentPaymentMethod: () => dispatch(resetCurrentPaymentMethod()),
  createPayment: () => dispatch(createPayment()),
  setFormSpecData: data => dispatch(setFormSpecData(data)),
  selectPaymentMethod: paymentData =>
    dispatch(selectPaymentMethod(paymentData)),
  debitPayment: paymentId => dispatch(debitPayment(paymentId)),
})

export default connect(null, mapDispatchToProps)(ApplicationDonePage)
